// Renders the real dartboard.svg artwork into a mount element and wires up
// click handlers on its scoring regions. Purely a rendering/input primitive -
// it knows nothing about tallies, rounds, or game rules; callers get an
// onTap(...) callback per dart and decide what to do with it.

const DIMMED_DARTBOARD_OPACITY = '0.3';

let dartboardSVGText = null;
let dartboardSVGPromise = null;

// Loads and caches the real dartboard.svg artwork so each mount can clone a
// fresh copy (a plain <img> can't have per-path click handlers or per-mode
// dimming, so the file is fetched as text and inlined instead).
function fetchDartboardSVG() {
    if (dartboardSVGText) return Promise.resolve(dartboardSVGText);
    if (dartboardSVGPromise) return dartboardSVGPromise;
    dartboardSVGPromise = fetch('dartboard.svg')
        .then(res => res.text())
        .then(text => { dartboardSVGText = text; return text; })
        .catch(err => { console.error('Failed to load dartboard.svg', err); dartboardSVGPromise = null; });
    return dartboardSVGPromise;
}
fetchDartboardSVG();

// dartboard.svg's own <defs> (the metallic-number gradient/bevel filter, the
// double-ring clip path) keep the same ids on every clone getFreshDartboardSVG
// makes - harmless with only one clone on the page, but some games (Halve
// It especially) can leave an earlier board sitting hidden in the DOM (e.g.
// a closed keypad modal - core/modal.js's hideAnimatedModal only hides it,
// never removes it) while a later mount clones a second one. Two elements
// sharing id="metallic-text" resolve any url(#metallic-text) reference to
// whichever comes first in the document - if that's the hidden one, the
// browser can fail to paint the reference at all (an observed bug: the
// wedge numbers went invisible while the plain hex-filled wedges/rings kept
// rendering fine, since only the numbers depend on a url(#id) reference).
// Suffixing every id - and every url(#id) attribute that points to it - with
// a per-clone counter keeps each clone's own <defs> entirely self-contained,
// regardless of how many other copies of this same SVG are already sitting
// in the DOM.
let dartboardCloneCounter = 0;

function uniquifyIds(svg) {
    const suffix = `-${dartboardCloneCounter++}`;
    const idMap = new Map();
    svg.querySelectorAll('[id]').forEach(el => {
        const newId = el.id + suffix;
        idMap.set(el.id, newId);
        el.id = newId;
    });
    if (!idMap.size) return;
    const urlPattern = /url\(#([^)]+)\)/g;
    ['fill', 'filter', 'clip-path', 'stroke', 'mask'].forEach(attr => {
        svg.querySelectorAll(`[${attr}*="url(#"]`).forEach(el => {
            const value = el.getAttribute(attr);
            el.setAttribute(attr, value.replace(urlPattern, (match, id) => idMap.has(id) ? `url(#${idMap.get(id)})` : match));
        });
    });
}

function getFreshDartboardSVG() {
    if (!dartboardSVGText) return null;
    const container = document.createElement('div');
    container.innerHTML = dartboardSVGText;
    const svg = container.querySelector('svg');
    svg.removeAttribute('id');
    svg.classList.add('dartboard-svg');
    uniquifyIds(svg);
    return svg;
}

// Draws a small permanent dot - like a real dart left sticking in the board -
// at the given point (in the SVG's own viewBox coordinate space) on whichever
// board is currently mounted. Sized relative to the SVG's current
// viewBox-units-per-rendered-pixel ratio, so it reads as the same real size
// whether the board is zoomed in on one wedge or shown in full.
export function drawDartMark(mount, x, y) {
    const svg = mount.querySelector('svg');
    if (!svg) return;
    const ctm = svg.getScreenCTM();
    if (!ctm) return;
    // Real screen-pixels-per-viewBox-unit, straight from the transform the
    // browser is actually applying - unlike deriving it from
    // viewBox.width/boundingRect.width, this accounts for any letterboxing
    // preserveAspectRatio adds when the mount's own box isn't the same
    // shape as the viewBox (Halve It's fixed-height mount rarely is,
    // Killer's square one usually is - hence the same "5px" reading very
    // differently sized between them before this fix).
    const pixelsPerUnit = ctm.a;
    const dot = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    dot.setAttribute('class', 'dart-tap-mark');
    dot.setAttribute('cx', x);
    dot.setAttribute('cy', y);
    dot.setAttribute('r', 5 / pixelsPerUnit);
    dot.setAttribute('stroke-width', 1 / pixelsPerUnit);
    svg.appendChild(dot);
}

// Marks where a Computer player actually decided to aim, before its dart
// lands there - only ever drawn in ?debugAim=1 (see core/debug.js's
// isAimDebugMode and components/cpu-dartboard-overlay.js's own use of this),
// so a decision that looks wrong can be checked visually against where the
// dart actually ends up (drawDartMark's own white dot) rather than only
// inferred from the resulting score. A solid yellow circle with the dart's
// own number in white inside it - distinct from drawDartMark's plain white
// "landed" dot - laid out with the same CTM scaling so it reads as the same
// real size regardless of zoom.
export function drawAimMarker(mount, x, y, dartNumber) {
    const svg = mount.querySelector('svg');
    if (!svg) return;
    const ctm = svg.getScreenCTM();
    if (!ctm) return;
    const pixelsPerUnit = ctm.a;
    const dot = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    dot.setAttribute('class', 'aim-marker');
    dot.setAttribute('cx', x);
    dot.setAttribute('cy', y);
    dot.setAttribute('r', 9 / pixelsPerUnit);
    svg.appendChild(dot);
    const label = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    label.setAttribute('class', 'aim-marker-label');
    label.setAttribute('x', x);
    label.setAttribute('y', y);
    label.setAttribute('font-size', 11 / pixelsPerUnit);
    label.textContent = String(dartNumber);
    svg.appendChild(label);
}

// Removes every dart-tap dot drawn on whichever board is currently mounted -
// for callers that clear/undo/restart something the dots were tracking
// (a game's Clear/Backspace, or a fresh turn) rather than relying on a
// mount's own next remount to (sometimes) wipe them incidentally.
export function clearDartTaps(mount) {
    const svg = mount.querySelector('svg');
    if (!svg) return;
    svg.querySelectorAll('.dart-tap-mark').forEach(el => el.remove());
}

// Removes only the most recently drawn dot - for a Backspace that undoes
// just the last dart, not every dart of the turn (each dot is appended in
// throw order, so the last one in the DOM is the last one thrown).
export function removeLastDartMark(mount) {
    const svg = mount.querySelector('svg');
    if (!svg) return;
    const marks = svg.querySelectorAll('.dart-tap-mark');
    if (marks.length) marks[marks.length - 1].remove();
}

// Converts a click event's screen coordinates into the currently-mounted
// SVG's own viewBox space and immediately draws a dot there - for most
// callers (the board stays mounted for a whole turn) that's the whole
// story, but a caller that remounts the board fresh after every single dart
// (Killer) needs the point itself back too, to redraw it (along with every
// other dart already thrown that turn) once the fresh board is in place.
function markTapFromEvent(mount, e) {
    const svg = mount.querySelector('svg');
    if (!svg) return null;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return null;
    const svgPoint = pt.matrixTransform(ctm.inverse());
    const point = { x: svgPoint.x, y: svgPoint.y };
    drawDartMark(mount, point.x, point.y);
    return point;
}

// Marks a scoring region as tappable and wires its click handler (handed the
// tapped point, in SVG space - most callers ignore it, since the dot's
// already drawn by the time this fires; Killer's onTap doesn't), plus an
// explicit pressed-state class for brief tap feedback. The CSS :active
// pseudo-class doesn't reliably apply to SVG shapes on mobile browsers
// (iOS Safari in particular needs a real touch listener present to ever
// paint :active at all), so the pressed state is driven directly off
// pointer events instead - down adds it, anything that ends the press
// (up, cancel, or the pointer sliding off the element) removes it.
function wireSegment(mount, el, onClick) {
    el.classList.add('dart-segment');
    el.addEventListener('pointerdown', () => el.classList.add('dart-segment-pressed'));
    const clearPressed = () => el.classList.remove('dart-segment-pressed');
    el.addEventListener('pointerup', clearPressed);
    el.addEventListener('pointercancel', clearPressed);
    el.addEventListener('pointerleave', clearPressed);
    el.addEventListener('click', (e) => onClick(markTapFromEvent(mount, e)));
}

function frameViewBox(svg, boxes, paddingRatio) {
    const x = Math.min(...boxes.map(b => b.x));
    const y = Math.min(...boxes.map(b => b.y));
    const right = Math.max(...boxes.map(b => b.x + b.width));
    const bottom = Math.max(...boxes.map(b => b.y + b.height));
    const padX = (right - x) * paddingRatio;
    const padY = (bottom - y) * paddingRatio;
    svg.setAttribute('viewBox', `${x - padX} ${y - padY} ${right - x + padX * 2} ${bottom - y + padY * 2}`);
}

// Clones the cached dartboard, zooms into `target`'s wedge, wires up click
// handlers on just its single/double/triple areas (onTap(multiplier)), and
// dims the rest. `isStillRelevant` guards the case where the SVG was still
// loading on first call - it's re-checked before the delayed retry mounts.
export function mountDartboardForTarget(mount, target, { onTap, isStillRelevant = () => true }) {
    if (!mount) return;

    const svg = getFreshDartboardSVG();
    if (!svg) {
        mount.textContent = 'Loading dartboard...';
        fetchDartboardSVG().then(() => {
            if (isStillRelevant()) mountDartboardForTarget(mount, target, { onTap, isStillRelevant });
        });
        return;
    }

    mount.innerHTML = '';
    mount.appendChild(svg);

    const activeSection = svg.querySelector(`g.section-${target}`);

    svg.querySelectorAll('g[class^="section-"]').forEach(section => {
        if (section === activeSection) {
            section.style.opacity = '1';
        } else {
            section.style.opacity = DIMMED_DARTBOARD_OPACITY;
            section.style.pointerEvents = 'none';
        }
    });
    svg.querySelectorAll('.outer-bull, .bullseye, .section-divider').forEach(el => {
        el.style.opacity = DIMMED_DARTBOARD_OPACITY;
    });
    let targetLabel = null;
    svg.querySelectorAll('text').forEach(label => {
        const isTarget = label.textContent.trim() === String(target);
        label.style.opacity = isTarget ? '1' : DIMMED_DARTBOARD_OPACITY;
        if (isTarget) targetLabel = label;
    });

    if (activeSection) {
        // DOM order within a section runs outer-ring, double-ring, single (outer
        // bed), triple-ring, single (inner bed) - the outer single sits between
        // the two scoring rings, the inner one hugs the bull.
        const singles = activeSection.querySelectorAll('.single');
        const outerSingle = singles[0];
        singles.forEach(el => wireSegment(mount, el, () => onTap(1)));
        const doubleRing = activeSection.querySelector('.double-ring');
        if (doubleRing) wireSegment(mount, doubleRing, () => onTap(2));
        const tripleRing = activeSection.querySelector('.triple-ring');
        if (tripleRing) wireSegment(mount, tripleRing, () => onTap(3));

        // Frame the zoom around the double ring, triple ring, and the single bed
        // between them, so both scoring rings stay fully visible - the inner
        // single near the bull is allowed to be cropped off-screen. Also include
        // the number label itself, whose real footprint (it's rotated in place
        // around its own x/y) is approximated as a square around that anchor.
        const boxes = [doubleRing, outerSingle, tripleRing].filter(Boolean).map(el => el.getBBox());
        if (targetLabel) {
            const lb = targetLabel.getBBox();
            const cx = parseFloat(targetLabel.getAttribute('x'));
            const cy = parseFloat(targetLabel.getAttribute('y'));
            const r = Math.hypot(lb.width, lb.height) / 2;
            boxes.push({ x: cx - r, y: cy - r, width: r * 2, height: r * 2 });
        }
        frameViewBox(svg, boxes, 0.15);
    }
}

// Clones the cached dartboard, zooms into the bull, wires up click handlers on
// the outer bull (onTap(1)) and bullseye (onTap(2)), and dims every
// wedge/divider.
export function mountDartboardForBull(mount, { onTap, isStillRelevant = () => true }) {
    if (!mount) return;

    const svg = getFreshDartboardSVG();
    if (!svg) {
        mount.textContent = 'Loading dartboard...';
        fetchDartboardSVG().then(() => {
            if (isStillRelevant()) mountDartboardForBull(mount, { onTap, isStillRelevant });
        });
        return;
    }

    mount.innerHTML = '';
    mount.appendChild(svg);

    svg.querySelectorAll('g[class^="section-"], .section-divider').forEach(el => {
        el.style.opacity = DIMMED_DARTBOARD_OPACITY;
        el.style.pointerEvents = 'none';
    });
    svg.querySelectorAll('text').forEach(label => {
        label.style.opacity = DIMMED_DARTBOARD_OPACITY;
    });

    const outerBull = svg.querySelector('.outer-bull');
    const bullseye = svg.querySelector('.bullseye');
    [outerBull, bullseye].forEach(el => { if (el) el.style.opacity = '1'; });

    if (outerBull) wireSegment(mount, outerBull, () => onTap(1));
    if (bullseye) wireSegment(mount, bullseye, () => onTap(2));

    frameViewBox(svg, [outerBull, bullseye].filter(Boolean).map(el => el.getBBox()), 0.3);
}

// Clones the cached dartboard at full size (no zoom/dimming) and wires every
// scoring region on the whole board, for games where a turn's darts can land
// on any number - onTap(number, multiplier), with number 1-20 or 25 for bull
// and multiplier 1/2/3 (bull: 1 = outer, 2 = bullseye).
//
// `readOnly` skips wiring entirely (no click handlers, no hover/press
// affordance - see wireSegment) so an observer sees exactly the same board,
// dimming, and dart marks as anyone else, but can't tap it. `onTap` is
// unused (and can be omitted) when readOnly.
export function mountDartboardFullBoard(mount, { onTap, readOnly = false }) {
    if (!mount) return;

    const svg = getFreshDartboardSVG();
    if (!svg) {
        mount.textContent = 'Loading dartboard...';
        fetchDartboardSVG().then(() => mountDartboardFullBoard(mount, { onTap, readOnly }));
        return;
    }

    mount.innerHTML = '';
    mount.appendChild(svg);

    if (readOnly) return;

    svg.querySelectorAll('g[class^="section-"]').forEach(section => {
        const match = /section-(\d+)/.exec(section.getAttribute('class') || '');
        if (!match) return;
        const number = Number(match[1]);

        section.querySelectorAll('.single').forEach(el => wireSegment(mount, el, (point) => onTap(number, 1, point)));
        const doubleRing = section.querySelector('.double-ring');
        if (doubleRing) wireSegment(mount, doubleRing, (point) => onTap(number, 2, point));
        const tripleRing = section.querySelector('.triple-ring');
        if (tripleRing) wireSegment(mount, tripleRing, (point) => onTap(number, 3, point));
    });

    const outerBull = svg.querySelector('.outer-bull');
    const bullseye = svg.querySelector('.bullseye');
    if (outerBull) wireSegment(mount, outerBull, (point) => onTap(25, 1, point));
    if (bullseye) wireSegment(mount, bullseye, (point) => onTap(25, 2, point));
}
